import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import { t } from 'i18next';
import BinaryControl from './components/BinaryControl';
import NumericControl from './components/NumericControl';
import EnumControl from './components/EnumControl';
import { featurePropTypes } from './types';
import CompositeControl from './components/CompositeControl';
import { setDeviceProperty } from '../../services/zigbeeService';
import { useGeneralContext } from '../../context/GeneralContext';
import { useActuatorContext } from '../../context/ActuatorContext';
import { useConfirmation } from '../../context/ConfirmationContext';
import { Zigbee2MQTTUtils } from '../../utils/zigbeeUtils';
import { useSensorReadingsContext } from '../../context/SensorReadingsContext';
import { useLocationsCacheContext } from '../../context/LocationsCacheContext';

const controlComponents = {
  binary: BinaryControl,
  numeric: NumericControl,
  enum: EnumControl,
  composite: CompositeControl,
};

const READING_TIMEOUT = 10_000; // ms

const ActuatorControl = ({
  gateway,
  deviceId,
  deviceName,
  feature,
  readOnly,
  mini,
}) => {
  const stateId = `${gateway}-${deviceId}-${feature.property}`;
  const { openConfirmation } = useConfirmation();
  const { openToast } = useGeneralContext();
  const { state, setCurrentValue, setCurrentConfirming } = useActuatorContext();
  const { connection, subscribe } = useSensorReadingsContext();
  const { refreshMap, isFetching: isFetchingLocationsCache } =
    useLocationsCacheContext();

  const setValue = useCallback(
    value => setCurrentValue(stateId, value),
    [setCurrentValue, stateId],
  );
  const setConfirming = useCallback(
    confirming => setCurrentConfirming(stateId, confirming),
    [setCurrentConfirming, stateId],
  );
  const { value = feature.reading?.value } = state[stateId] ?? {};
  const featureRef = useRef(feature);
  const timeoutRef = useRef(null);
  const valueRef = useRef(value);

  useEffect(() => {
    featureRef.current = feature;
  }, [feature]);

  const checkReadingSync = useCallback(
    earlyCheck => {
      const feature = featureRef.current;
      let latestReading = feature.reading?.value;
      const sentReading = valueRef.current;
      if (
        feature.type === 'binary' &&
        typeof latestReading === 'number' &&
        typeof sentReading !== 'number'
      ) {
        latestReading = latestReading ? feature.value_on : feature.value_off;
      }

      if (!earlyCheck && connection.status === 'Disconnected') {
        openConfirmation({
          message: t(
            'components.actuator_control.command_verification_fail_message',
            {
              feature: feature.label,
              device: deviceName,
            },
          ),
          preventClickOutside: true,
          handleClose: () => {
            refreshMap();
            setConfirming(false);
          },
          handleAction: () => {
            subscribe();
            refreshMap();
            setConfirming(false);
          },
          title: t('components.actuator_control.command_verification_fail'),
          actionLabel: t('components.actuator_control.connect_real_time'),
        });
        return;
      }

      if (!earlyCheck && !feature.equals(latestReading, sentReading)) {
        setValue(latestReading);
        setConfirming('failed');
        openConfirmation({
          message: t('components.actuator_control.command_fail_message', {
            feature: feature.label,
            device: deviceName,
          }),
          preventClickOutside: true,
          hideCancel: true,
          handleAction: () =>
            setTimeout(() => {
              setConfirming(false);
            }, 3000),
          title: t('components.actuator_control.command_fail'),
          actionLabel: 'OK',
        });
      } else if (feature.equals(latestReading, sentReading)) {
        setConfirming('confirmed');
        setTimeout(() => {
          setConfirming(false);
        }, 3000);
        openToast(
          t('components.actuator_control.command_confirmed', {
            feature: feature.label,
            device: deviceName,
          }),
          'success',
        );
        if (earlyCheck && timeoutRef.current) clearTimeout(timeoutRef.current);
      } else {
        setValue(latestReading);
      }
      timeoutRef.current = null;
    },
    [
      deviceName,
      openConfirmation,
      openToast,
      connection.status,
      subscribe,
      setConfirming,
      setValue,
      refreshMap,
    ],
  );

  useEffect(() => {
    if (isFetchingLocationsCache) return;
    if (timeoutRef.current == null) setValue(feature.reading?.value);
    else checkReadingSync(true);
  }, [
    checkReadingSync,
    feature.reading?.value,
    isFetchingLocationsCache,
    setValue,
  ]);

  const actuate = useMemo(
    () =>
      debounce(
        async (
          deviceName,
          featureName,
          value,
          gateway,
          deviceId,
          checkReadingSync,
          openToast,
          setConfirming,
          setValue,
          connectionStatus,
        ) => {
          const f = featureRef.current;
          try {
            setConfirming('sending');
            valueRef.current = value;
            await setDeviceProperty(gateway, deviceId, {
              [featureName]: f.mapValueDto(value),
            });
            setConfirming('confirming');
            openToast(
              t('components.actuator_control.command_success', {
                feature: featureName,
                device: deviceName,
              }),
            );
            if (Zigbee2MQTTUtils.hasState(f.access)) {
              if (timeoutRef.current) clearTimeout(timeoutRef.current);
              timeoutRef.current = setTimeout(
                () => checkReadingSync(false),
                connectionStatus === 'Disconnected' ? 0 : READING_TIMEOUT,
              );
            } else {
              setConfirming(false);
            }
          } catch {
            openToast(
              t('components.actuator_control.command_error', {
                feature: featureName,
                device: deviceName,
              }),
              'error',
            );
            setValue(f.reading?.value);
            setConfirming(false);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
        },
        1000,
      ),
    [],
  );

  useEffect(() => {
    return () => {
      actuate.cancel();
    };
  }, [actuate]);

  const handleChange = (featureName, value) => {
    if (readOnly) return null;
    if (gateway)
      actuate(
        deviceName,
        featureName,
        value,
        gateway,
        deviceId,
        checkReadingSync,
        openToast,
        setConfirming,
        setValue,
        connection.status,
      );
    setValue(value);
  };

  const ControlStrategy = controlComponents[feature.type];
  if (!ControlStrategy) return null;

  return (
    <ControlStrategy
      feature={feature}
      value={value}
      mini={mini}
      handleChange={handleChange}
    />
  );
};

ActuatorControl.defaultProps = {
  readOnly: false,
};

ActuatorControl.propTypes = {
  gateway: PropTypes.string,
  deviceId: PropTypes.string,
  deviceName: PropTypes.string,
  readOnly: PropTypes.bool,
  mini: PropTypes.bool,
  feature: featurePropTypes.isRequired,
};

export default ActuatorControl;
